import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, Mail } from 'lucide-react';
import ContactForm from '../components/ContactForm';
import { Toaster } from '../components/ui/sonner';

const Contact = () => {
  return (
    <div data-testid="contact-page">
      <Toaster position="top-center" />
      
      {/* Hero Section */}
      <section className="py-24 bg-navy" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="font-manrope text-cyan mb-4">Get In Touch</p>
            <h1 className="font-oswald uppercase text-5xl md:text-6xl font-bold text-white mb-6">
              Contact Us
            </h1>
            <p className="font-manrope text-slate-300 text-xl max-w-2xl mx-auto">
              Ready to start your project? Have a question? We're here to help.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-white" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <h2 className="section-title text-3xl mb-8">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-orange rounded-md">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-oswald uppercase font-bold text-navy mb-1">Address</h3>
                    <p className="font-manrope text-slate-600">
                      7012 6th St. N.<br />
                      Oakdale, MN 55128
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-orange rounded-md">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-oswald uppercase font-bold text-navy mb-1">Phone</h3>
                    <a 
                      href="tel:651-738-9354" 
                      className="font-manrope text-slate-600 hover:text-orange transition-colors"
                      data-testid="contact-phone-link"
                    >
                      (651) 738-9354
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-orange rounded-md">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-oswald uppercase font-bold text-navy mb-1">Business Hours</h3>
                    <p className="font-manrope text-slate-600">
                      Monday - Friday: 7AM - 4PM
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-orange rounded-md">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-oswald uppercase font-bold text-navy mb-1">Service Area</h3>
                    <p className="font-manrope text-slate-600">
                      Minneapolis, St. Paul & the<br />
                      entire Twin Cities region
                    </p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="mt-8 rounded-md overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2822.0!2d-92.9!3d44.9!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDTCsDU0JzAwLjAiTiA5MsKwNTQnMDAuMCJX!5e0!3m2!1sen!2sus!4v1600000000000!5m2!1sen!2sus"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="BNPH Location"
                  data-testid="contact-map"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <div className="bg-slate-50 p-8 md:p-12 rounded-md">
                <h2 className="section-title text-3xl mb-2">Send Us a Message</h2>
                <p className="font-manrope text-slate-600 mb-8">
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>
                <ContactForm />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
